import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG } from '../config/api.config';

export type AuthSessionResponse = {
    access_token: string;
    token_type?: string;
    expires_in?: number;
    user?: any;
    org?: any | null;
};

export type ResetPasswordPhoneResponse =
    | AuthSessionResponse
    | { ok: true; email_verification_required?: boolean };

export type PolicyAcceptancePayload = {
    accepted: boolean;
    version: string;
    flow_source: 'signup_partner' | 'signup_investor' | 'invite_completion';
    policy_keys?: string[];
};

@Injectable({ providedIn: 'root' })
export class AuthApiService {
    private http = inject(HttpClient);

    sendOtp(phoneE164: string): Observable<{ success: boolean; to: string; status: string }> {
        return this.http.post<any>(
            `${API_CONFIG.AUTH_URL}/otp/send`,
            { phone: phoneE164 },
            { withCredentials: true }
        );
    }

    verifyOtp(phoneE164: string, code: string): Observable<AuthSessionResponse> {
        return this.http
            .post<any>(
                `${API_CONFIG.AUTH_URL}/otp/verify`,
                { phone: phoneE164, code },
                { withCredentials: true }
            )
            .pipe(map((res) => this.normalizeSession(res)));
    }

    sendProfilePhoneOtp(phoneE164: string, targetKey: 'primary' | 'secondary' = 'primary'): Observable<{ phone: string | null; phone_key: string | null; phone_masked: string | null }> {
        return this.http.post<any>(`${API_CONFIG.API_BASE_URL}/API2/users/me/phone-otp/send`, {
            phone: phoneE164,
            phone_key: targetKey
        }).pipe(
            map((res) => res?.data ?? res ?? {}),
            map((data: any) => ({
                phone: data?.phone ?? null,
                phone_key: data?.phone_key ?? null,
                phone_masked: data?.phone_masked ?? null
            }))
        );
    }

    verifyProfilePhoneOtp(
        phoneE164: string,
        code: string,
        targetKey: 'primary' | 'secondary' = 'primary'
    ): Observable<any> {
        return this.http.post<any>(`${API_CONFIG.API_BASE_URL}/API2/users/me/phone-otp/verify`, {
            phone: phoneE164,
            code,
            phone_key: targetKey
        });
    }

    signInWithEmail(email: string, password: string): Observable<AuthSessionResponse> {
        return this.http
            .post<any>(
                `${API_CONFIG.AUTH_URL}/signin`,
                { email, password },
                { withCredentials: true }
            )
            .pipe(map((res) => this.normalizeSession(res)));
    }

    signInWithPhone(phoneE164: string, password: string): Observable<AuthSessionResponse> {
        return this.http
            .post<any>(
                `${API_CONFIG.AUTH_URL}/signin`,
                { phone: phoneE164, password },
                { withCredentials: true }
            )
            .pipe(map((res) => this.normalizeSession(res)));
    }

    sendEmailOtp(email: string): Observable<{ ok: true }> {
        return this.http.post<any>(
            `${API_CONFIG.AUTH_URL}/otp/email/send`,
            { email },
            { withCredentials: true }
        );
    }

    verifyEmailOtp(email: string, code: string): Observable<AuthSessionResponse> {
        return this.http
            .post<any>(
                `${API_CONFIG.AUTH_URL}/otp/email/verify`,
                { email, code },
                { withCredentials: true }
            )
            .pipe(map((res) => this.normalizeSession(res)));
    }

    signUpInvestor(payload: {
        email: string;
        phone?: string | null;
        password?: string | null;
        first_name?: string | null;
        last_name?: string | null;
        full_name?: string | null;
        arabic_full_name?: string | null;
        return_url?: string | null;
        policy_acceptance: PolicyAcceptancePayload;
    }): Observable<any> {
        return this.http.post<any>(`${API_CONFIG.AUTH_URL}/signup/investor`, payload, { withCredentials: true });
    }

    signUpPartner(payload: {
        email: string;
        phone?: string | null;
        password?: string | null;
        org_name: string;
        first_name?: string | null;
        last_name?: string | null;
        full_name?: string | null;
        arabic_full_name?: string | null;
        return_url?: string | null;
        policy_acceptance: PolicyAcceptancePayload;
    }): Observable<any> {
        return this.http.post<any>(`${API_CONFIG.AUTH_URL}/signup/partner`, payload, { withCredentials: true });
    }

    resendConfirmEmail(payload: { signup_session_id: string; return_url?: string | null }): Observable<{ ok: true; email_sent?: boolean }> {
        return this.http.post<any>(`${API_CONFIG.AUTH_URL}/resend-confirm-email`, payload, { withCredentials: true });
    }

    confirmEmail(token: string): Observable<AuthSessionResponse> {
        return this.http
            .post<any>(`${API_CONFIG.AUTH_URL}/confirm-email`, { token }, { withCredentials: true })
            .pipe(map((res) => this.normalizeSession(res)));
    }

    forgotPassword(payload: { email?: string; phone?: string }): Observable<{ ok: true }> {
        return this.http.post<any>(`${API_CONFIG.AUTH_URL}/forgot-password`, payload, { withCredentials: true });
    }

    resetPassword(
        token: string,
        newPassword: string,
        policy_acceptance?: PolicyAcceptancePayload | null
    ): Observable<{ ok: true }> {
        return this.http.post<any>(
            `${API_CONFIG.AUTH_URL}/reset-password`,
            { token, new_password: newPassword, policy_acceptance: policy_acceptance || undefined },
            { withCredentials: true }
        );
    }

    resetPasswordPhone(phoneE164: string, code: string, newPassword: string): Observable<ResetPasswordPhoneResponse> {
        return this.http
            .post<any>(
                `${API_CONFIG.AUTH_URL}/reset-password/phone`,
                { phone: phoneE164, code, new_password: newPassword },
                { withCredentials: true }
            )
            .pipe(
                map((res) => {
                    const accessToken =
                        res?.access_token ||
                        res?.accessToken ||
                        res?.data?.access_token ||
                        res?.data?.accessToken ||
                        null;
                    if (accessToken) {
                        return this.normalizeSession(res);
                    }
                    if (res?.ok) {
                        return {
                            ok: true,
                            email_verification_required: Boolean(res?.email_verification_required)
                        };
                    }
                    throw new Error('Missing access_token');
                })
            );
    }

    refresh(): Observable<AuthSessionResponse> {
        // vNext contract: refresh token is in httpOnly cookie; body should be empty.
        return this.http
            .post<any>(`${API_CONFIG.AUTH_URL}/refresh`, {}, { withCredentials: true })
            .pipe(map((res) => this.normalizeSession(res)));
    }

    signOut(fcmToken?: string | null): Observable<{ ok: true }> {
        const body = fcmToken ? { fcm_token: fcmToken } : {};
        return this.http.post<any>(`${API_CONFIG.AUTH_URL}/signout`, body, { withCredentials: true });
    }

    private normalizeSession(raw: any): AuthSessionResponse {
        const accessToken =
            raw?.access_token ||
            raw?.accessToken ||
            raw?.data?.access_token ||
            raw?.data?.accessToken ||
            null;

        if (!accessToken) {
            throw new Error('Missing access_token');
        }

        return {
            access_token: String(accessToken),
            token_type: raw?.token_type || raw?.tokenType || 'Bearer',
            expires_in: raw?.expires_in || raw?.expiresIn || undefined,
            user: raw?.user || raw?.data?.user || undefined,
            org: raw?.org || raw?.data?.org || null
        };
    }
}
